import { ReactElement } from 'react';

import { DocplannerDoctorMappingSetCommand } from '@dreryk/egabinet-docplanner-frontend-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { dispatchSnackbarPositiveEvent, ModalConfirmButton } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { doctorsMappingsTranslationKeys } from '../../../translations';

import { AddDoctorAssociationFormBean, AddDoctorAssociationFormController } from './addDoctorAssociation.types';

type AddDoctorAssociationModalConfirmButtonProps = {
   readonly handleSubmit: AddDoctorAssociationFormController['handleSubmit'];
   readonly docplannerDoctorId: string;
   readonly onSuccess: VoidFunction;
};

const useDocplannerDoctorMappingSetCommand = (
   docplannerDoctorId: string,
   onSuccess: VoidFunction
): UseCqrsCommandResult<void, AddDoctorAssociationFormBean, DocplannerDoctorMappingSetCommand> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new DocplannerDoctorMappingSetCommand();
         command.externalDoctorId = docplannerDoctorId;
         command.practitionerId = Slot.emptyIfNull(parameters.eGabinetPractitioner?.id ?? null);
         command.associations = parameters.docplannerDoctorAddressMappings.map((mapping) => {
            return {
               docplannerAddressId: mapping.externalAddressId,
               location: Slot.emptyIfNull(
                  mapping.healthcareProviderLocation
                     ? {
                          locationId: mapping.healthcareProviderLocation.locationIdentifier.locationId,
                          type: mapping.healthcareProviderLocation.locationIdentifier.type,
                       }
                     : null
               ),
            };
         });
         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(doctorsMappingsTranslationKeys.associationDataHasBeenSaved);
            onSuccess();
         },
      }
   );
};

export const AddDoctorAssociationModalConfirmButton = (
   props: AddDoctorAssociationModalConfirmButtonProps
): ReactElement => {
   const { executeAsync: addDocplannerDoctorAssociation } = useDocplannerDoctorMappingSetCommand(
      props.docplannerDoctorId,
      props.onSuccess
   );
   return (
      <ModalConfirmButton
         label={doctorsMappingsTranslationKeys.save}
         onClick={props.handleSubmit(addDocplannerDoctorAssociation)}
      />
   );
};
